import { useEffect, useState } from "react";
import Shell from "../components/Shell.jsx";
import { api } from "../lib/api.js";
import { centsToDisplay, dollarsToCents, todayISO } from "../lib/money.js";
import { Field, inputCls, SelectWithOther } from "../components/ui.jsx";
import { PAYMENT_METHODS } from "../components/TransactionForm.jsx";

// Expense categories for the pickers, plus the payment methods the ledger
// form offers — the same list, so a recurring template can name the same card
// an entry would.
function useAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [methods, setMethods] = useState(PAYMENT_METHODS);
  useEffect(() => {
    api("/api/accounts")
      .then((d) => {
        setAccounts((d.accounts || []).filter((a) => a.type === "expense"));
        const custom = (d.custom_options || []).filter((o) => o.field === "payment_method").map((o) => o.value);
        setMethods([...PAYMENT_METHODS, ...custom.filter((c) => !PAYMENT_METHODS.includes(c))]);
      })
      .catch(() => {});
  }, []);
  return { accounts, methods };
}

/* ---------------- Home office ---------------- */
function HomeOfficeCard({ home, onSaved }) {
  const [office, setOffice] = useState(home.office_sqft ? String(home.office_sqft) : "");
  const [homeSq, setHomeSq] = useState(home.home_sqft ? String(home.home_sqft) : "");
  const [err, setErr] = useState("");

  async function save() {
    if (Number(office) === home.office_sqft && Number(homeSq) === home.home_sqft) return;
    setErr("");
    try {
      await api("/api/deductions/home", { method: "PUT", body: { office_sqft: Number(office) || 0, home_sqft: Number(homeSq) || 0 } });
      onSaved();
    } catch (e) {
      setErr(e.message || "Save failed");
    }
  }

  return (
    <section className="space-y-2 rounded-2xl bg-white p-4 shadow-sm">
      <p className="text-sm font-semibold text-slate-700">🏠 Home office — {home.year}</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Office sq ft">
          <input value={office} onChange={(e) => setOffice(e.target.value)} onBlur={save} inputMode="decimal" className={inputCls} placeholder="e.g. 150" />
        </Field>
        <Field label="Whole home sq ft">
          <input value={homeSq} onChange={(e) => setHomeSq(e.target.value)} onBlur={save} inputMode="decimal" className={inputCls} placeholder="e.g. 2200" />
        </Field>
      </div>
      {err && <p className="text-xs text-red-600">{err}</p>}
      {home.office_sqft > 0 && (
        <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm">
          <p>
            <span className="font-semibold text-navy-700">{centsToDisplay(home.simplified_annual_cents)}/yr</span>
            <span className="text-slate-500"> simplified method (${home.simplified_rate_cents / 100}/sq ft, {home.simplified_max_sqft} max)</span>
          </p>
          {home.business_pct != null && (
            <p className="mt-1 text-xs text-slate-500">
              Business use {home.business_pct}% of the home — the actual-expense method applies that share to home costs
              (utilities, insurance, repairs). Your CPA picks whichever wins.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

/* ---------------- Recurring ---------------- */
function RecurringForm({ accounts, methods, initial, onDone, onCancel }) {
  const [f, setF] = useState(
    initial
      ? { ...initial, amount: (initial.amount_cents / 100).toFixed(2) }
      : { vendor: "", amount: "", cadence: "monthly", account_id: "exp_software", business_use_pct: "100", mode: "track", next_due: todayISO(), payment_method: "" },
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api("/api/deductions/recurring", {
        method: "POST",
        body: {
          action: initial ? "update" : "create",
          id: initial?.id,
          vendor: f.vendor,
          amount_cents: dollarsToCents(f.amount),
          cadence: f.cadence,
          account_id: f.account_id,
          business_use_pct: Number(f.business_use_pct),
          mode: f.mode,
          next_due: f.next_due,
          payment_method: f.payment_method,
        },
      });
      onDone();
    } catch (e2) {
      setErr(e2.message || "Save failed");
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3 rounded-2xl bg-white p-4 shadow-sm">
      <p className="text-sm font-semibold text-slate-700">{initial ? "Edit" : "Add"} recurring expense</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Vendor">
          <input value={f.vendor} onChange={set("vendor")} className={inputCls} placeholder="e.g. Adobe" />
        </Field>
        <Field label="Amount ($)">
          <input value={f.amount} onChange={set("amount")} inputMode="decimal" className={inputCls} placeholder="e.g. 22.99" />
        </Field>
        <Field label="How often">
          <select value={f.cadence} onChange={set("cadence")} className={inputCls}>
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </select>
        </Field>
        <Field label="Category">
          <select value={f.account_id} onChange={set("account_id")} className={inputCls}>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Business use %">
          <input value={f.business_use_pct} onChange={set("business_use_pct")} inputMode="numeric" className={inputCls} />
        </Field>
        <Field label="Posting">
          <select value={f.mode} onChange={set("mode")} className={inputCls}>
            <option value="track">Track only (comes via bank import)</option>
            <option value="auto">Auto-post to the books</option>
          </select>
        </Field>
      </div>
      <Field
        label={f.mode === "auto" ? "Paid from (required)" : "Paid from"}
        hint={
          f.mode === "auto"
            ? "Every auto-posted entry is booked as paid from here. Left blank, the books would count it as bank money."
            : "Optional for tracked charges — the bank import carries the account that paid."
        }
      >
        <SelectWithOther value={f.payment_method || ""} onChange={(v) => setF((s) => ({ ...s, payment_method: v }))} options={methods} />
      </Field>
      {f.mode === "auto" && (
        <>
          <Field label="Next due date">
            <input type="date" value={f.next_due || ""} onChange={set("next_due")} className={inputCls} />
          </Field>
          <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Auto-post is for costs that never hit an imported bank/card account — if this charge shows up in your VIA
            import, use Track only, or it will be counted twice.
          </p>
        </>
      )}
      {err && <p className="text-sm text-red-600">{err}</p>}
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="flex-1 rounded-xl border border-slate-300 py-3 text-sm font-semibold text-slate-500">
          Cancel
        </button>
        <button disabled={busy} className="flex-1 rounded-xl bg-navy-700 py-3 text-sm font-semibold text-white disabled:opacity-50">
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}

/* ---------------- Assets ---------------- */
const LIFE_OPTIONS = [
  [3, "3 yr — software/tooling"],
  [5, "5 yr — computers, cameras, equipment"],
  [7, "7 yr — furniture, fixtures"],
  [10, "10 yr"],
];

function AssetForm({ initial, onDone, onCancel }) {
  const [f, setF] = useState(
    initial
      ? { ...initial, cost: (initial.cost_cents / 100).toFixed(2) }
      : { name: "", cost: "", placed_in_service: todayISO(), business_use_pct: "100", life_years: 5, sec179_candidate: 1, notes: "" },
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api("/api/deductions/assets", {
        method: "POST",
        body: {
          action: initial ? "update" : "create",
          id: initial?.id,
          name: f.name,
          cost_cents: dollarsToCents(f.cost),
          placed_in_service: f.placed_in_service,
          business_use_pct: Number(f.business_use_pct),
          life_years: Number(f.life_years),
          sec179_candidate: Number(f.sec179_candidate) ? 1 : 0,
          notes: f.notes,
        },
      });
      onDone();
    } catch (e2) {
      setErr(e2.message || "Save failed");
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3 rounded-2xl bg-white p-4 shadow-sm">
      <p className="text-sm font-semibold text-slate-700">{initial ? "Edit" : "Add"} asset</p>
      <Field label="What is it">
        <input value={f.name} onChange={set("name")} className={inputCls} placeholder="e.g. MacBook Pro 16&quot;" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Cost ($)">
          <input value={f.cost} onChange={set("cost")} inputMode="decimal" className={inputCls} placeholder="e.g. 2499.00" />
        </Field>
        <Field label="In service since">
          <input type="date" value={f.placed_in_service} onChange={set("placed_in_service")} className={inputCls} />
        </Field>
        <Field label="Business use %">
          <input value={f.business_use_pct} onChange={set("business_use_pct")} inputMode="numeric" className={inputCls} />
        </Field>
        <Field label="Useful life">
          <select value={f.life_years} onChange={set("life_years")} className={inputCls}>
            {LIFE_OPTIONS.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-600">
        <input
          type="checkbox"
          checked={!!Number(f.sec179_candidate)}
          onChange={(e) => setF((s) => ({ ...s, sec179_candidate: e.target.checked ? 1 : 0 }))}
        />
        Flag as a §179 / bonus-depreciation candidate for the CPA
      </label>
      {err && <p className="text-sm text-red-600">{err}</p>}
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="flex-1 rounded-xl border border-slate-300 py-3 text-sm font-semibold text-slate-500">
          Cancel
        </button>
        <button disabled={busy} className="flex-1 rounded-xl bg-navy-700 py-3 text-sm font-semibold text-white disabled:opacity-50">
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}

/* ---------------- Page ---------------- */
export default function Deductions() {
  const { accounts, methods } = useAccounts();
  const [data, setData] = useState(null);
  const [addingRec, setAddingRec] = useState(false);
  const [editingRec, setEditingRec] = useState(null);
  const [addingAsset, setAddingAsset] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);

  const load = () => api("/api/deductions").then(setData).catch(() => setData(undefined));
  useEffect(() => {
    load();
  }, []);

  if (!data)
    return (
      <Shell title="Deductions" back>
        <p className="text-center text-sm text-slate-400">Loading…</p>
      </Shell>
    );

  async function removeRec(id) {
    if (!window.confirm("Remove this recurring expense?")) return;
    await api("/api/deductions/recurring", { method: "POST", body: { action: "delete", id } });
    load();
  }
  async function removeAsset(id) {
    if (!window.confirm("Delete this asset from the register?")) return;
    await api("/api/deductions/assets", { method: "POST", body: { action: "delete", id } });
    load();
  }

  return (
    <Shell title="Deductions" back>
      <HomeOfficeCard home={data.home} onSaved={load} />

      <section>
        <div className="mb-1 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            Recurring · {centsToDisplay(data.recurring_monthly_cents)}/mo deductible
          </p>
          <button onClick={() => { setAddingRec(true); setEditingRec(null); }} className="text-xs font-semibold text-navy-700">
            + Add
          </button>
        </div>
        {(addingRec || editingRec) && (
          <RecurringForm
            accounts={accounts}
            methods={methods}
            initial={editingRec}
            onDone={() => { setAddingRec(false); setEditingRec(null); load(); }}
            onCancel={() => { setAddingRec(false); setEditingRec(null); }}
          />
        )}
        {data.recurring.length > 0 && (
          <div className="divide-y divide-slate-100 rounded-2xl bg-white shadow-sm">
            {data.recurring.map((r) => (
              <div key={r.id} className="flex items-center gap-2 px-4 py-3">
                <button onClick={() => { setEditingRec(r); setAddingRec(false); }} className="min-w-0 flex-1 text-left">
                  <p className="truncate text-sm font-medium text-slate-800">{r.vendor}</p>
                  <p className="text-xs text-slate-400">
                    {centsToDisplay(r.amount_cents)}/{r.cadence === "annual" ? "yr" : "mo"}
                    {r.business_use_pct < 100 ? ` · ${r.business_use_pct}% business` : ""}
                    {r.mode === "auto" ? ` · auto-posts${r.next_due ? ` (next ${r.next_due})` : ""}` : ""}
                    {r.payment_method ? ` · ${r.payment_method}` : ""}
                  </p>
                  {r.mode === "auto" && !r.payment_method && (
                    <p className="text-xs font-semibold text-red-600">No payment method — will not post again until one is set. Tap to fix.</p>
                  )}
                </button>
                <span className="shrink-0 text-sm font-semibold text-slate-700">{centsToDisplay(r.monthly_cents)}/mo</span>
                <button onClick={() => removeRec(r.id)} className="shrink-0 text-xs text-slate-300">
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="mb-1 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            Assets · ~{centsToDisplay(data.assets_this_year_cents)} this yr
          </p>
          <button onClick={() => { setAddingAsset(true); setEditingAsset(null); }} className="text-xs font-semibold text-navy-700">
            + Add
          </button>
        </div>
        {(addingAsset || editingAsset) && (
          <AssetForm
            initial={editingAsset}
            onDone={() => { setAddingAsset(false); setEditingAsset(null); load(); }}
            onCancel={() => { setAddingAsset(false); setEditingAsset(null); }}
          />
        )}
        {data.assets.length === 0 && !addingAsset ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
            Computers, cameras, furniture — anything over ~$2,500 your CPA should depreciate.
          </div>
        ) : (
          <div className="divide-y divide-slate-100 rounded-2xl bg-white shadow-sm">
            {data.assets.map((a) => (
              <div key={a.id} className="flex items-center gap-2 px-4 py-3">
                <button onClick={() => { setEditingAsset(a); setAddingAsset(false); }} className="min-w-0 flex-1 text-left">
                  <p className="truncate text-sm font-medium text-slate-800">
                    {a.name}
                    {a.sec179_candidate ? <span className="ml-2 rounded-full bg-navy-700/10 px-2 py-0.5 text-[10px] font-semibold text-navy-700">§179</span> : null}
                    {a.disposed_at ? <span className="ml-2 text-xs text-slate-400">disposed</span> : null}
                  </p>
                  <p className="text-xs text-slate-400">
                    {centsToDisplay(a.cost_cents)} · in service {a.placed_in_service} · {a.life_years} yr
                    {a.business_use_pct < 100 ? ` · ${a.business_use_pct}% business` : ""}
                  </p>
                </button>
                <span className="shrink-0 text-sm font-semibold text-slate-700">{a.this_year_est_cents ? `~${centsToDisplay(a.this_year_est_cents)}` : "—"}</span>
                <button onClick={() => removeAsset(a.id)} className="shrink-0 text-xs text-slate-300">
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <p className="text-center text-xs text-slate-400">{data.disclaimer}</p>
    </Shell>
  );
}
